import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import Footer from "../components/Footer";
import { ComuneSelect } from "../components/ComuneSelect";
import { getProvinceCoperte } from "../lib/istat";
import api from "../lib/api";

const IMG = "https://images.pexels.com/photos/3306957/pexels-photo-3306957.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const resolveLogo = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

export default function Agencies() {
  const [items, setItems] = useState([]);
  const [province, setProvince] = useState([]);
  const [filters, setFilters] = useState({ provincia: "", comune: "" });

  useEffect(() => { api.get("/public/agencies").then((r) => setItems(r.data)); }, []);
  useEffect(() => { getProvinceCoperte().then(setProvince); }, []);

  const filtered = useMemo(() => {
    return items.filter((a) => {
      if (filters.provincia && a.provincia !== filters.provincia) return false;
      if (filters.comune && a.comune !== filters.comune) return false;
      return true;
    });
  }, [items, filters]);

  const resetFilters = () => setFilters({ provincia: "", comune: "" });
  const hasFilters = filters.provincia || filters.comune;

  return (
    <div className="paper-bg" data-testid="agencies-page">
      <PublicNav />
      <section className="max-w-[1240px] mx-auto px-6 md:px-10 pt-16 pb-10 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <div className="label-uppercase">Le agenzie</div>
          <h1 className="font-serif text-5xl md:text-6xl mt-3">Agenzie Partner.</h1>
          <p className="text-[#5C5657] mt-4 max-w-xl">Solo agenzie che applicano il Protocollo Pronto alla Vendita, radicate sul territorio di Vicenza e provincia.</p>
        </div>
        <div className="md:col-span-5"><img src={IMG} alt="Vicenza" className="w-full aspect-[4/3] object-cover border border-[#E6E4E0]" /></div>
      </section>

      {/* Filtri territorio */}
      <section className="max-w-[1240px] mx-auto px-6 md:px-10 pb-6">
        <div className="border border-[#E6E4E0] bg-white p-5 flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="label-uppercase">Provincia</label>
            <select
              data-testid="filter-provincia"
              value={filters.provincia}
              onChange={(e) => setFilters({ provincia: e.target.value, comune: "" })}
              className="editorial-input mt-2"
            >
              <option value="">Tutte le province</option>
              {province.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="label-uppercase">Comune</label>
            <div className="mt-2">
              <ComuneSelect
                value={filters.comune}
                onChange={(v) => setFilters({ ...filters, comune: v })}
                provincia={filters.provincia || null}
                testid="filter-comune"
                placeholder="Tutti i comuni · digita per cercare"
              />
            </div>
          </div>
          <div>
            {hasFilters && (
              <button
                data-testid="reset-filters"
                onClick={resetFilters}
                className="text-xs label-uppercase text-[#5C5657] hover:text-[#7D2E3F] px-3 py-3"
              >
                Reset
              </button>
            )}
          </div>
        </div>
        {hasFilters && (
          <div className="text-xs text-[#5C5657] mt-3">
            {filtered.length} agenzia{filtered.length !== 1 ? "e" : ""} · filtro: {filters.comune ? `${filters.comune} (${filters.provincia})` : filters.provincia}
          </div>
        )}
      </section>

      <section className="max-w-[1240px] mx-auto px-6 md:px-10 pb-24">
        {filtered.length === 0 ? (
          <div className="border border-[#E6E4E0] py-20 text-center text-[#5C5657]">
            {hasFilters ? "Nessuna agenzia partner in questa zona." : "Nessuna agenzia partner al momento."}
          </div>
        ) : (
          <div className="divide-y divide-[#E6E4E0] border-y border-[#E6E4E0]" data-testid="agencies-list">
            {filtered.map((a) => {
              const logo = resolveLogo(a.logo_url || a.logo);
              return (
                <div key={a.id} data-testid={`agency-${a.id}`} className="py-8 grid md:grid-cols-12 gap-6 items-start">
                  {/* Logo */}
                  <div className="md:col-span-2">
                    <div className="w-full aspect-square bg-white border border-[#E6E4E0] flex items-center justify-center overflow-hidden">
                      {logo ? (
                        <img src={logo} alt={`Logo ${a.name}`} className="w-full h-full object-contain p-3" />
                      ) : (
                        <div className="font-serif text-4xl text-[#E6E4E0] tracking-[0.1em]">
                          {(a.name || "?").slice(0, 2).toUpperCase()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Anagrafica */}
                  <div className="md:col-span-4">
                    <h3 className="font-serif text-3xl leading-tight">{a.name}</h3>
                    {a.stato_rete && (
                      <div className="mt-3">
                        <span className={`badge-stamp text-[10px] ${a.stato_rete === "Fondatore" ? "badge-pronto" : "badge-criticita"}`}>
                          {a.stato_rete === "Fondatore" ? "Partner Fondatore" : "Partner"}
                        </span>
                      </div>
                    )}
                    <div className="text-xs text-[#5C5657] mt-3">
                      {a.address ? a.address : `${a.comune} · ${a.provincia}`}
                    </div>
                    {(a.phone || a.email) && (
                      <div className="text-xs text-[#5C5657] mt-2 space-y-1">
                        {a.phone && <div>tel {a.phone}</div>}
                        {a.email && <div><a href={`mailto:${a.email}`} className="hover:text-[#7D2E3F]">{a.email}</a></div>}
                      </div>
                    )}
                  </div>

                  {/* Agenti */}
                  <div className="md:col-span-4 md:pt-3">
                    {a.agents?.filter(ag => ag.name).length > 0 && (
                      <div className="space-y-3">
                        {a.agents.filter(ag => ag.name).map((ag, i) => (
                          <div key={i} className="text-sm">
                            <div className="text-[#3A3D42]">{ag.name}</div>
                            {(ag.phone || ag.email) && (
                              <div className="text-xs text-[#5C5657] mt-0.5">
                                {ag.phone}
                                {ag.phone && ag.email && " – "}
                                {ag.email && <a href={`mailto:${ag.email}`} className="hover:text-[#7D2E3F]">{ag.email}</a>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="md:col-span-2 md:text-right md:pt-3">
                    {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="btn-secondary text-xs">Sito web</a>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Sezione riservata Agenzie — accesso all'area partner */}
      <section className="border-t border-[#E6E4E0] bg-[#FDFDFC]" data-testid="section-area-agenzie">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="label-uppercase text-[#7D2E3F] mb-3">Area riservata</div>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight text-[#3A3D42]">
              Sei un'agenzia della rete?
            </h2>
            <p className="text-[#5C5657] mt-4 max-w-2xl leading-relaxed">
              Accedi all'area riservata per gestire i tuoi immobili in Galleria,
              le richieste in arrivo e il profilo della tua agenzia.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/area-agenzie/login"
              data-testid="agenzie-partner-cta"
              className="btn-primary inline-block"
            >
              Accedi all'Area Agenzie →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
